

import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, MapPin, MapPinned, Phone } from "lucide-react";
import indiamap from "../../../../assets/images/Indiamap.png";
import stateframe from "../../../../assets/images/stateframe.png";
import mapSvg from "../../../../assets/images/map.svg";
import DistrictMap, { getDistrictMapUrl, getDistrictPaths } from "./DistrictMap";
import scaPartnersData from "./scaPartnersData";
import AllScasModal from "../../../../components/AllScasModal";

const SVG_MAP_WIDTH = 374;
const SVG_MAP_HEIGHT = 426;
const BASE_STATE_COLOR = "#b7cec6";
const ACTIVE_STATE_COLOR = "#1e3a5f";
const HOVER_STATE_COLOR = ACTIVE_STATE_COLOR;
const MARKER_COLOR = "#f26f4f";
const TOOLTIP_WIDTH = 126;
const TOOLTIP_HEIGHT = 34;

const STATE_NAME_BY_PATH_INDEX = {
  1: "Andaman and Nicobar Islands",
  3: "Telangana",
  4: "Andhra Pradesh",
  6: "Arunachal Pradesh",
  7: "Assam",
  8: "Bihar",
  9: "Chandigarh",
  10: "Chhattisgarh",
  11: "Dadra & Nagar Haveli and Daman & Diu",
  12: "Delhi",
  13: "Goa",
  14: "Gujarat",
  16: "Haryana",
  17: "Himachal Pradesh",
  18: "Jharkhand",
  19: "Karnataka",
  20: "Kerala",
  22: "Madhya Pradesh",
  23: "Maharashtra",
  24: "Manipur",
  25: "Meghalaya",
  26: "Mizoram",
  27: "Nagaland",
  28: "Odisha",
  30: "Puducherry",
  32: "Punjab",
  33: "Rajasthan",
  34: "Sikkim",
  35: "Tamil Nadu",
  36: "Tripura",
  37: "Uttar Pradesh",
  38: "Uttarakhand",
  39: "West Bengal",
  41: "Lakshadweep",
  43: "Jammu & Kashmir",
  44: "Ladakh",
};

const STATE_MARKERS = {
  "Andaman and Nicobar Islands": { x: 320, y: 372 },
  "Andhra Pradesh": { x: 161, y: 306 },
  "Arunachal Pradesh": { x: 336, y: 138 },
  Assam: { x: 315, y: 166 },
  Bihar: { x: 225, y: 169 },
  Chandigarh: { x: 110, y: 98 },
  Chhattisgarh: { x: 181, y: 238 },
  "Dadra & Nagar Haveli and Daman & Diu": { x: 63, y: 248 },
  Delhi: { x: 114, y: 129 },
  Goa: { x: 75, y: 313 },
  Gujarat: { x: 41, y: 217 },
  Haryana: { x: 101, y: 120 },
  "Himachal Pradesh": { x: 117, y: 82 },
  Jharkhand: { x: 223, y: 200 },
  "Jammu & Kashmir": { x: 89, y: 53 },
  Karnataka: { x: 105, y: 317 },
  Kerala: { x: 102, y: 376 },
  Ladakh: { x: 106, y: 37 },
  Lakshadweep: { x: 61, y: 384 },
  "Madhya Pradesh": { x: 131, y: 196 },
  Maharashtra: { x: 110, y: 266 },
  Manipur: { x: 328, y: 185 },
  Meghalaya: { x: 296, y: 173 },
  Mizoram: { x: 315, y: 206 },
  Nagaland: { x: 334, y: 166 },
  Odisha: { x: 208, y: 248 },
  Puducherry: { x: 135, y: 334 },
  Punjab: { x: 93, y: 94 },
  Rajasthan: { x: 73, y: 158 },
  Sikkim: { x: 259, y: 144 },
  "Tamil Nadu": { x: 129, y: 373 },
  Telangana: { x: 145, y: 280 },
  Tripura: { x: 301, y: 199 },
  "Uttar Pradesh": { x: 162, y: 150 },
  Uttarakhand: { x: 142, y: 108 },
  "West Bengal": { x: 251, y: 190 },
};

const getMapSearchUrl = (address) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;

const parseSvgPaths = (svgText) => {
  const parsedSvg = new DOMParser().parseFromString(svgText, "image/svg+xml");

  return Array.from(parsedSvg.querySelectorAll("path")).map((path, index) => ({
    d: path.getAttribute("d") || "",
    hasFill: path.hasAttribute("fill"),
    hasStroke: path.hasAttribute("stroke"),
    stateName: STATE_NAME_BY_PATH_INDEX[index + 1] || "",
  }));
};

const getTooltipPosition = ({ x, y }) => ({
  x: Math.min(Math.max(x + 12, 4), SVG_MAP_WIDTH - TOOLTIP_WIDTH - 4),
  y: Math.min(Math.max(y - 42, 4), SVG_MAP_HEIGHT - TOOLTIP_HEIGHT - 4),
});

const getTooltipLabel = (stateName) => {
  const label = `${stateName}:`;

  return label.length > 26 ? `${label.slice(0, 23)}...` : label;
};

const ScasPartnersContent = () => {
  const [selectedState, setSelectedState] = useState(null);
  const [hoveredState, setHoveredState] = useState(null);
  const [mapPaths, setMapPaths] = useState([]);
  const [districtMapsByState, setDistrictMapsByState] = useState({});
  const [districtMapStatus, setDistrictMapStatus] = useState("idle");
  const [showAllScasModal, setShowAllScasModal] = useState(false);
  const handleOpenAllScas = () => setShowAllScasModal(true);
  const handleCloseAllScas = () => setShowAllScasModal(false);

  const scasByState = useMemo(
    () =>
      scaPartnersData.reduce((stateMap, sca) => {
        if (!stateMap[sca.state]) {
          stateMap[sca.state] = [];
        }

        stateMap[sca.state].push(sca);
        return stateMap;
      }, {}),
    []
  );

  const selectedScas = selectedState ? scasByState[selectedState] || [] : [];
  const markerEntries = useMemo(() => Object.entries(STATE_MARKERS), []);
  const hoveredMarker = hoveredState ? STATE_MARKERS[hoveredState] : null;
  const tooltipPosition = hoveredMarker ? getTooltipPosition(hoveredMarker) : null;
  const selectedDistrictMap = selectedState ? districtMapsByState[selectedState] : null;
  const selectedDistrictPaths = useMemo(
    () => getDistrictPaths(selectedDistrictMap),
    [selectedDistrictMap]
  );

  useEffect(() => {
    let isMounted = true;

    fetch(mapSvg)
      .then((response) => response.text())
      .then((svgText) => {
        if (isMounted) {
          setMapPaths(parseSvgPaths(svgText));
        }
      })
      .catch(() => {
        if (isMounted) {
          setMapPaths([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedState) {
      setDistrictMapStatus("idle");
      return undefined;
    }

    if (districtMapsByState[selectedState]) {
      setDistrictMapStatus("loaded");
      return undefined;
    }

    let isMounted = true;
    setDistrictMapStatus("loading");

    fetch(getDistrictMapUrl(selectedState))
      .then((response) => {
        if (!response.ok) {
          throw new Error("District map not available");
        }

        return response.json();
      })
      .then((geoJson) => {
        if (!isMounted) return;

        setDistrictMapsByState((currentMaps) => ({
          ...currentMaps,
          [selectedState]: geoJson,
        }));
        setDistrictMapStatus("loaded");
      })
      .catch(() => {
        if (isMounted) {
          setDistrictMapStatus("error");
        }
      });

    return () => {
      isMounted = false;
    };
  }, [districtMapsByState, selectedState]);

  const handleStateSelect = (stateName) => {
    if (!stateName) return;

    setSelectedState(stateName);
    setHoveredState(null);
  };

  const handleBackToIndiaMap = () => {
    setSelectedState(null);
    setHoveredState(null);
  };

  const handleStateLeave = (stateName) => {
    setHoveredState((currentState) => (currentState === stateName ? null : currentState));
  };

  return <div className="flex w-full flex-col gap-6 lg:flex-row">
          {/* View All Scas button when no state selected */}
          {!selectedState && (
            <button
              type="button"
              className="absolute top-8 right-8 bg-[#eef7f6] text-[#1e3a5f] hover:bg-[#dce8e8] px-4 py-2 rounded-md font-semibold"
              onClick={handleOpenAllScas}
            >
              View All SCAs
            </button>
          )}
      <div className="relative flex min-h-[520px] flex-1 items-center justify-center rounded-2xl bg-white p-6 shadow-xl">
        {selectedState && (
          <button
            type="button"
            className="absolute left-8 top-8 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#eef7f6] text-[#1e3a5f] hover:bg-[#dce8e8] focus:outline-none"
            onClick={handleBackToIndiaMap}
            aria-label="Back to India map"
            title="Back to India map"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2.5} />
          </button>
        )}

        <span className="absolute right-8 top-8 text-xl font-bold text-[#1e3a5f]">
          {selectedState || "India"}
        </span>

        <div
          className={`relative mt-10 w-full max-w-[435px] ${
            selectedState ? "aspect-[420/260]" : "aspect-[374/426]"
          }`}
        >
          {selectedState ? (
            <DistrictMap
              districts={selectedDistrictPaths}
              stateName={selectedState}
              status={districtMapStatus}
            />
          ) : mapPaths.length > 0 ? (
            <svg
              viewBox={`0 0 ${SVG_MAP_WIDTH} ${SVG_MAP_HEIGHT}`}
              className="absolute inset-0 h-full w-full"
              role="img"
              aria-label="Map of India"
            >
              <defs>
                <filter id="state-tooltip-shadow" x="-10%" y="-25%" width="120%" height="150%">
                  <feDropShadow
                    dx="0"
                    dy="2"
                    stdDeviation="2"
                    floodColor="#000000"
                    floodOpacity="0.22"
                  />
                </filter>
              </defs>

              {mapPaths.map(({ d, hasFill, hasStroke, stateName }, index) => {
                const isClickable = Boolean(stateName);
                const isSelected = selectedState === stateName;
                const isHovered = hoveredState === stateName;
                const fillColor = hasFill
                  ? isSelected
                    ? ACTIVE_STATE_COLOR
                    : isHovered
                    ? HOVER_STATE_COLOR
                    : BASE_STATE_COLOR
                  : "none";

                return (
                  <path
                    key={`${stateName || "map-path"}-${index}`}
                    d={d}
                    fill={fillColor}
                    stroke={hasStroke ? "white" : "none"}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      pointerEvents: isClickable ? "auto" : "none",
                      outline: "none",
                    }}
                    className={isClickable ? "cursor-pointer transition-colors duration-150" : ""}
                    role={isClickable ? "button" : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    aria-label={isClickable ? stateName : undefined}
                    onClick={isClickable ? () => handleStateSelect(stateName) : undefined}
                    onMouseEnter={isClickable ? () => setHoveredState(stateName) : undefined}
                    onMouseLeave={isClickable ? () => handleStateLeave(stateName) : undefined}
                    onFocus={isClickable ? () => setHoveredState(stateName) : undefined}
                    onBlur={isClickable ? () => handleStateLeave(stateName) : undefined}
                    onKeyDown={
                      isClickable
                        ? (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              handleStateSelect(stateName);
                            }
                          }
                        : undefined
                    }
                  />
                );
              })}

              {markerEntries.map(([stateName, marker]) => (
                <g
                  key={`${stateName}-marker`}
                  role="button"
                  tabIndex={0}
                  aria-label={stateName}
                  className="cursor-pointer"
                  transform={`translate(${marker.x} ${marker.y})`}
                  onClick={() => handleStateSelect(stateName)}
                  onMouseEnter={() => setHoveredState(stateName)}
                  onMouseLeave={() => handleStateLeave(stateName)}
                  onFocus={() => setHoveredState(stateName)}
                  onBlur={() => handleStateLeave(stateName)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleStateSelect(stateName);
                    }
                  }}
                >
                  <circle r="6" fill={MARKER_COLOR} opacity="0.28" pointerEvents="none">
                    <animate
                      attributeName="r"
                      values="5;10;5"
                      dur="1.4s"
                      repeatCount="indefinite"
                    />
                    <animate
                      attributeName="opacity"
                      values="0.36;0;0.36"
                      dur="1.4s"
                      repeatCount="indefinite"
                    />
                  </circle>
                  <circle r="3.4" fill={MARKER_COLOR} pointerEvents="none" />
                  <circle r="10" fill="transparent" pointerEvents="all" />
                </g>
              ))}

              {hoveredState && tooltipPosition && (
                <g
                  transform={`translate(${tooltipPosition.x} ${tooltipPosition.y})`}
                  pointerEvents="none"
                >
                  <rect
                    width={TOOLTIP_WIDTH}
                    height={TOOLTIP_HEIGHT}
                    rx="4"
                    fill="#ffffff"
                    stroke="#d9e2e2"
                    filter="url(#state-tooltip-shadow)"
                  />
                  <circle cx="12" cy="17" r="2.8" fill="#d9e2e2" />
                  <text
                    x="22"
                    y="21"
                    fill="#2d3c44"
                    fontSize="11"
                    fontWeight="600"
                    fontFamily="Inter, Arial, sans-serif"
                  >
                    {getTooltipLabel(hoveredState)}
                  </text>
                </g>
              )}
            </svg>
          ) : (
            <img
              src={indiamap}
              alt="Map of India"
              className="absolute inset-0 h-full w-full object-contain"
            />
          )}
        </div>
      </div>

      <div className="flex min-h-[520px] flex-1 flex-col rounded-2xl bg-white p-7 shadow-xl relative">
          { !selectedState && (
            <button
              className="absolute top-4 right-4 bg-[#1e3a5f] text-white px-4 py-2 rounded-md hover:bg-[#162c48] transition"
              onClick={handleOpenAllScas}
            >
              View All SCAs
            </button>
          ) }

        {selectedState ? (
          <>
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="text-[15px] font-semibold uppercase tracking-wide text-[#f58220]">
                  {selectedState}
                </p>
                <h2 className="mt-1 text-2xl font-bold leading-tight text-[#183967]">
                  State Channelising Agencies
                </h2>
              </div>
              <span className="rounded-full bg-[#eef7f6] px-3 py-1 text-sm font-bold text-[#005856]">
                {selectedScas.length} SCA{selectedScas.length === 1 ? "" : "s"}
              </span>
            </div>

            {selectedScas.length > 0 ? (
              <div className="max-h-[420px] space-y-5 overflow-y-auto pr-2">
                {selectedScas.map((sca) => (
                  <article key={sca.id} className="border-b border-[#dce8e8] pb-5 last:border-b-0">
                    <h3 className="text-xl font-bold leading-snug text-[#183967]">{sca.name}</h3>

                    <p className="mt-1 text-[15px] font-semibold text-[#183967]">State: {sca.state}</p>

                    <div className="mt-5 flex items-start gap-4 text-[#111827]">
                      <MapPin className="mt-1 h-7 w-7 flex-none text-[#f58220]" strokeWidth={3} />
                      <p className="text-[15px] leading-[1.7]">{sca.address}</p>
                    </div>

                    <div className="mt-5 flex items-center gap-4 text-[#111827]">
                      <Phone className="h-7 w-7 flex-none text-[#f58220]" strokeWidth={3} />
                      <p className="text-[15px] leading-[1.7]">{sca.contact || "Not available"}</p>
                    </div>

                    <a
                      href={getMapSearchUrl(sca.address)}
                      aria-label={`Locate Us: ${sca.name}, ${sca.state} on Google Maps (opens in a new tab)`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-5 inline-flex items-center gap-4 text-base font-semibold text-[#0048ff] hover:underline"
                    >
                      <MapPinned className="h-7 w-7 flex-none text-[#f58220]" strokeWidth={2.5} />
                      Locate Us
                    </a>
                  </article>
                ))}
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center text-center text-base font-semibold text-[#1e3a5f]">
                SCA details are not available for this state.
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-1 flex-col items-center justify-center text-center">
            <img
              src={stateframe}
              alt="State selection"
              className="mb-2 object-contain"
            />

            <h2 className="max-w-md text-lg font-semibold text-[#1e3a5f]">
              Please select the state from the map to know about your State Channelising Agencies
              (SCAs)
            </h2>
          </div>
        )}
      </div>
      {showAllScasModal && (
        <AllScasModal isOpen={showAllScasModal} onClose={handleCloseAllScas} data={scaPartnersData} />
      )}
    </div>
  

};

export default ScasPartnersContent;
